import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Calendar, Clock, Mail, Plus, Trash2, Copy, ExternalLink, Settings, FileText, LayoutGrid, Pencil, Filter, UserPlus, Edit3, Printer } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import HelperModal from '../components/HelperModal';

interface SlotConfig {
  id: number;
  title: string;
  description: string | null;
  slot_duration_minutes: number;
  appointment_type: 'parent_teacher_conference' | 'office_hours';
  is_active: boolean;
  public_token: string;
  created_at: string;
  updated_at: string;
  global_event_id?: number | null;
}

interface GlobalEvent {
  id: number;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  created_by: number;
  public_token: string | null;
  created_at: string;
  teacher_count: number;
  created_by_first_name?: string;
  created_by_last_name?: string;
}

interface TeacherOption {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string;
}

interface Slot {
  id: number;
  start_time: string;
  end_time: string;
  status: 'available' | 'reserved' | 'booked' | 'cancelled' | 'completed';
  reserved_until: string | null;
  booking_id?: number;
  parent_name?: string;
  parent_email?: string;
  student_name?: string | null;
  student_id?: string | null;
  booking_status?: string;
  notes?: string | null;
}

export default function Appointments() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrSchulleitung = (user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'schulleitung');
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showGlobalEventModal, setShowGlobalEventModal] = useState(false);
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [matrixEventId, setMatrixEventId] = useState<number | null>(null);
  const [matrixFilterTeacherIds, setMatrixFilterTeacherIds] = useState<number[]>([]);
  const [matrixFilterStatus, setMatrixFilterStatus] = useState<'all' | 'free' | 'booked'>('all');
  const [matrixFilterSearch, setMatrixFilterSearch] = useState('');
  const [manualSlotId, setManualSlotId] = useState<number | null>(null);
  const [editBookingSlot, setEditBookingSlot] = useState<Slot | null>(null);
  const [manualForm, setManualForm] = useState({ student_name: '', student_id: '', notes: '', parent_name: '', parent_email: '' });
  const [editForm, setEditForm] = useState({ student_name: '', student_id: '', notes: '', parent_name: '' });
  const [showManualStudentDropdown, setShowManualStudentDropdown] = useState(false);
  const [showEditStudentDropdown, setShowEditStudentDropdown] = useState(false);
  const manualStudentDropdownRef = useRef<HTMLDivElement>(null);
  const editStudentDropdownRef = useRef<HTMLDivElement>(null);
  const [debouncedManualStudentQuery, setDebouncedManualStudentQuery] = useState('');
  const [debouncedEditStudentQuery, setDebouncedEditStudentQuery] = useState('');
  const [globalEventForm, setGlobalEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    slot_duration_minutes: 15,
    teacher_ids: [] as number[],
    create_public_token: false
  });

  // Get teacher name for preset - only use first_name and last_name, fallback to username if not available
  const teacherName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : (user?.username || '');

  // Helper function to generate title based on appointment type
  const generateTitle = (appointmentType: 'parent_teacher_conference' | 'office_hours', name: string): string => {
    const typeLabel = appointmentType === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde';
    return name ? `${typeLabel} - ${name}` : typeLabel;
  };

  // Form state for new config
  const [newConfig, setNewConfig] = useState({
    title: '',
    description: '',
    slot_duration_minutes: 15,
    appointment_type: 'parent_teacher_conference' as 'parent_teacher_conference' | 'office_hours'
  });

  // Track if we've initialized the form to prevent re-initialization
  const [formInitialized, setFormInitialized] = useState(false);

  // Prefill title and description with teacher name when modal opens
  useEffect(() => {
    if (showCreateModal && teacherName && !formInitialized) {
      const defaultTitle = generateTitle(newConfig.appointment_type, teacherName);
      setNewConfig(prev => ({
        ...prev,
        title: defaultTitle,
        description: teacherName
      }));
      setFormInitialized(true);
    } else if (!showCreateModal) {
      // Reset when modal closes
      setFormInitialized(false);
    }
  }, [showCreateModal, teacherName, formInitialized, newConfig.appointment_type]);

  // Update title when appointment type changes (only if title matches the auto-generated pattern)
  useEffect(() => {
    if (showCreateModal && teacherName && newConfig.title && formInitialized) {
      const currentTypeLabel = newConfig.appointment_type === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde';
      const otherTypeLabel = newConfig.appointment_type === 'parent_teacher_conference' ? 'Sprechstunde' : 'Elternsprechtag';
      
      // Check if title contains the other type (needs to be updated)
      if (newConfig.title.includes(`${otherTypeLabel} - ${teacherName}`) || 
          newConfig.title === `${otherTypeLabel} - ${teacherName}`) {
        const newTitle = generateTitle(newConfig.appointment_type, teacherName);
        setNewConfig(prev => ({ ...prev, title: newTitle }));
      }
    }
  }, [newConfig.appointment_type]);

  // Form state for new slots - separated for better UX
  const [newSlots, setNewSlots] = useState({
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: ''
  });

  // Fetch all configs
  const { data: configs, isLoading: configsLoading } = useQuery<SlotConfig[]>(
    ['appointments', 'configs'],
    async () => {
      const response = await api.get('/appointments/configs');
      return response.data.data;
    }
  );

  // Fetch slots for selected config
  const { data: slots, isLoading: slotsLoading } = useQuery<Slot[]>(
    ['appointments', 'slots', selectedConfigId],
    async () => {
      if (!selectedConfigId) return [];
      const response = await api.get(`/appointments/configs/${selectedConfigId}/slots`);
      return response.data.data;
    },
    { enabled: !!selectedConfigId }
  );

  // Debounce Schüler-Suche für manuellen Eintrag / Bearbeiten
  useEffect(() => {
    if (manualSlotId == null) return;
    const t = setTimeout(() => setDebouncedManualStudentQuery(manualForm.student_name), 300);
    return () => clearTimeout(t);
  }, [manualSlotId, manualForm.student_name]);
  useEffect(() => {
    if (editBookingSlot == null) return;
    const t = setTimeout(() => setDebouncedEditStudentQuery(editForm.student_name), 300);
    return () => clearTimeout(t);
  }, [editBookingSlot, editForm.student_name]);

  // Schüler aus Datenbank suchen (für manuellen Eintrag / Bearbeiten)
  interface StudentSearchHit { id: string; name: string; klasse: string }
  type StudentSearchRow = { ID: number; sophomorixUnid: string; Vorname: string; Name: string; Klasse: string };
  const { data: manualStudentSearchResult } = useQuery(
    ['students', 'appointments-search', debouncedManualStudentQuery],
    () => api.get('/students', { params: { search: debouncedManualStudentQuery, sortField: 'Name', sortDirection: 'asc' } }),
    { enabled: manualSlotId != null && debouncedManualStudentQuery.trim().length >= 2 }
  );
  const { data: editStudentSearchResult } = useQuery(
    ['students', 'appointments-search', debouncedEditStudentQuery],
    () => api.get('/students', { params: { search: debouncedEditStudentQuery, sortField: 'Name', sortDirection: 'asc' } }),
    { enabled: editBookingSlot != null && debouncedEditStudentQuery.trim().length >= 2 }
  );
  const manualStudentSuggestions: StudentSearchHit[] = useMemo(() => {
    const body = manualStudentSearchResult?.data?.data ?? manualStudentSearchResult?.data;
    const list: StudentSearchRow[] = Array.isArray(body) ? body : (body as { data?: StudentSearchRow[] })?.data ?? [];
    return list.map((s: StudentSearchRow) => ({
      id: s.sophomorixUnid,
      name: [s.Vorname, s.Name].filter(Boolean).join(' ').trim() || String(s.sophomorixUnid),
      klasse: s.Klasse || ''
    }));
  }, [manualStudentSearchResult]);
  const { data: manualContactEmailData, isLoading: manualContactEmailsLoading } = useQuery(
    ['appointments', 'manual-contact-emails', manualForm.student_id],
    async () => {
      const response = await api.get(`/appointments/students/${encodeURIComponent(manualForm.student_id)}/contact-emails`);
      return response.data?.data as { emails: string[]; auto_email: string | null; requires_selection: boolean };
    },
    { enabled: manualSlotId != null && !!manualForm.student_id?.trim() }
  );
  const manualContactEmails = manualContactEmailData?.emails || [];
  const manualAutoEmail = manualContactEmailData?.auto_email || null;
  const manualRequiresEmailSelection = manualContactEmailData?.requires_selection || false;

  useEffect(() => {
    if (manualSlotId == null) return;
    if (!manualForm.student_id?.trim()) return;
    if (manualContactEmails.length === 1 && manualAutoEmail) {
      setManualForm((f) => ({ ...f, parent_email: manualAutoEmail }));
      return;
    }
    if (manualContactEmails.length > 1) {
      const current = (manualForm.parent_email || '').trim().toLowerCase();
      const stillValid = manualContactEmails.some((e) => e.toLowerCase() === current);
      if (!stillValid) {
        setManualForm((f) => ({ ...f, parent_email: '' }));
      }
    }
  }, [manualSlotId, manualForm.student_id, manualContactEmails, manualAutoEmail]);
  const editStudentSuggestions: StudentSearchHit[] = useMemo(() => {
    const body = editStudentSearchResult?.data?.data ?? editStudentSearchResult?.data;
    const list: StudentSearchRow[] = Array.isArray(body) ? body : (body as { data?: StudentSearchRow[] })?.data ?? [];
    return list.map((s: StudentSearchRow) => ({
      id: s.sophomorixUnid,
      name: [s.Vorname, s.Name].filter(Boolean).join(' ').trim() || String(s.sophomorixUnid),
      klasse: s.Klasse || ''
    }));
  }, [editStudentSearchResult]);

  // Click outside: Dropdowns schließen
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (manualStudentDropdownRef.current && !manualStudentDropdownRef.current.contains(e.target as Node)) setShowManualStudentDropdown(false);
      if (editStudentDropdownRef.current && !editStudentDropdownRef.current.contains(e.target as Node)) setShowEditStudentDropdown(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Globale Anlässe (nur Admin/Schulleitung)
  const { data: globalEvents, isLoading: globalEventsLoading } = useQuery<GlobalEvent[]>(
    ['appointments', 'global', 'events'],
    async () => {
      const response = await api.get('/appointments/global/events');
      return response.data.data;
    },
    { enabled: isAdminOrSchulleitung }
  );
  const { data: teachersForGlobal } = useQuery<TeacherOption[]>(
    ['appointments', 'global', 'teachers'],
    async () => {
      const response = await api.get('/appointments/global/teachers');
      return response.data.data;
    },
    { enabled: isAdminOrSchulleitung && (showGlobalEventModal || editingEventId != null) }
  );

  const { data: editingEventData } = useQuery<{ event: GlobalEvent; teacher_ids: number[] }>(
    ['appointments', 'global', 'event', editingEventId],
    async () => {
      const response = await api.get(`/appointments/global/events/${editingEventId}`);
      return response.data.data;
    },
    { enabled: isAdminOrSchulleitung && editingEventId != null }
  );

  useEffect(() => {
    if (!editingEventData || !editingEventId) return;
    const e = editingEventData.event;
    const start = e.start_time ? new Date(e.start_time) : null;
    const end = e.end_time ? new Date(e.end_time) : null;
    setGlobalEventForm({
      title: e.title || '',
      description: e.description || '',
      start_date: start ? start.toISOString().slice(0, 10) : '',
      start_time: start ? String(start.getHours()).padStart(2, '0') + ':' + String(start.getMinutes()).padStart(2, '0') : '',
      end_date: end ? end.toISOString().slice(0, 10) : '',
      end_time: end ? String(end.getHours()).padStart(2, '0') + ':' + String(end.getMinutes()).padStart(2, '0') : '',
      slot_duration_minutes: e.slot_duration_minutes ?? 15,
      teacher_ids: [...(editingEventData.teacher_ids || [])],
      create_public_token: !!e.public_token
    });
  }, [editingEventData, editingEventId]);

  interface MatrixCell { config_id: number; teacher_name: string; slot_id: number | null; status: string | null; parent_name: string | null; student_name: string | null; student_id: string | null }
  interface MatrixRow { time: string; cells: MatrixCell[] }
  const { data: matrixData, isLoading: matrixLoading } = useQuery<{ event: GlobalEvent; teachers: { config_id: number; teacher_name: string }[]; timeSlots: string[]; rows: MatrixRow[] }>(
    ['appointments', 'global', 'matrix', matrixEventId],
    async () => {
      const response = await api.get(`/appointments/global/events/${matrixEventId}/matrix`);
      return response.data.data;
    },
    { enabled: isAdminOrSchulleitung && matrixEventId != null }
  );

  // Matrix-Filter: sichtbare Lehrer und Zeilen
  const matrixVisibleTeachers = useMemo(() => {
    if (!matrixData?.teachers) return [];
    if (matrixFilterTeacherIds.length === 0) return matrixData.teachers;
    return matrixData.teachers.filter((t) => matrixFilterTeacherIds.includes(t.config_id));
  }, [matrixData?.teachers, matrixFilterTeacherIds]);

  useEffect(() => {
    if (matrixEventId != null) {
      setMatrixFilterTeacherIds([]);
      setMatrixFilterStatus('all');
      setMatrixFilterSearch('');
    }
  }, [matrixEventId]);

  const matrixVisibleRows = useMemo(() => {
    if (!matrixData?.rows) return [];
    const searchLower = matrixFilterSearch.trim().toLowerCase();
    return matrixData.rows.filter((row) => {
      if (matrixFilterStatus === 'free') {
        const hasFree = row.cells.some((c) => c.slot_id != null && c.status !== 'booked' && c.status !== 'reserved');
        if (!hasFree) return false;
      } else if (matrixFilterStatus === 'booked') {
        const hasBooked = row.cells.some((c) => c.status === 'booked' || c.status === 'reserved');
        if (!hasBooked) return false;
      }
      if (searchLower) {
        const matchesSearch = row.cells.some(
          (c) =>
            (c.parent_name && c.parent_name.toLowerCase().includes(searchLower)) ||
            (c.student_name && c.student_name.toLowerCase().includes(searchLower))
        );
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [matrixData?.rows, matrixFilterStatus, matrixFilterSearch]);

  // Create config mutation
  const createConfigMutation = useMutation(
    async (data: typeof newConfig) => {
      const response = await api.post('/appointments/configs', data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments', 'configs']);
        setShowCreateModal(false);
        setFormInitialized(false);
        setNewConfig({ title: '', description: '', slot_duration_minutes: 15, appointment_type: 'parent_teacher_conference' });
        toast.success('Anlass/Event erstellt');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
      }
    }
  );

  // Helper function to generate time options (00:00 to 23:30 in 15-minute intervals)
  const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeStr);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Helper function to combine date and time into ISO string
  const combineDateTime = (date: string, time: string): string => {
    if (!date || !time) return '';
    // Ensure time format is HH:mm (it already is from dropdown)
    return `${date}T${time}`;
  };

  // Create slots mutation
  const createSlotsMutation = useMutation(
    async (data: { configId: number; start_time: string; end_time: string }) => {
      const response = await api.post(`/appointments/configs/${data.configId}/slots`, {
        start_time: data.start_time,
        end_time: data.end_time
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        setShowSlotModal(false);
        setNewSlots({ start_date: '', start_time: '', end_date: '', end_time: '' });
        toast.success(`${data.count} Slots erstellt`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Erstellen der Slots');
      }
    }
  );

  // Delete config mutation
  const deleteConfigMutation = useMutation(
    async (id: number) => {
      await api.delete(`/appointments/configs/${id}`);
    },
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(['appointments', 'configs']);
        if (selectedConfigId === id) {
          setSelectedConfigId(null);
        }
        toast.success('Anlass/Event gelöscht');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Löschen');
      }
    }
  );

  // Send link mutation
  const sendLinkMutation = useMutation(
    async (data: { configId: number; recipient_email: string; message?: string }) => {
      await api.post(`/appointments/configs/${data.configId}/send-link`, {
        recipient_email: data.recipient_email,
        message: data.message
      });
    },
    {
      onSuccess: () => {
        setShowEmailModal(false);
        setEmailRecipient('');
        setEmailMessage('');
        toast.success('E-Mail versendet');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Versenden');
      }
    }
  );

  // Delete booking mutation
  const deleteBookingMutation = useMutation(
    async (bookingId: number) => {
      await api.delete(`/appointments/bookings/${bookingId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        toast.success('Termin storniert');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Löschen');
      }
    }
  );

  // Manual booking (Schüler/Notiz manuell eintragen für freien Slot)
  const manualBookingMutation = useMutation(
    async (payload: { slotId: number; student_name?: string; student_id?: string; notes?: string; parent_name?: string; parent_email?: string }) => {
      const res = await api.post(`/appointments/slots/${payload.slotId}/manual-booking`, {
        student_name: payload.student_name || undefined,
        student_id: payload.student_id || undefined,
        notes: payload.notes || undefined,
        parent_name: payload.parent_name || undefined,
        parent_email: payload.parent_email || undefined
      });
      return res.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        setManualSlotId(null);
        setManualForm({ student_name: '', student_id: '', notes: '', parent_name: '', parent_email: '' });
        setShowManualStudentDropdown(false);
        if (data?.data?.contact_email_used) {
          toast.success(
            data?.data?.notification_email_sent
              ? `Schüler/Notiz eingetragen. Hinweis-Mail gesendet an ${data.data.contact_email_used}`
              : `Schüler/Notiz eingetragen. E-Mail an ${data.data.contact_email_used} konnte nicht versendet werden`
          );
        } else {
          toast.success('Schüler/Notiz eingetragen');
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Eintragen');
      }
    }
  );

  // Update booking (Schüler/Notiz bei gebuchtem Termin bearbeiten)
  const updateBookingMutation = useMutation(
    async (payload: { bookingId: number; student_name?: string; student_id?: string; notes?: string; parent_name?: string }) => {
      await api.patch(`/appointments/bookings/${payload.bookingId}`, {
        student_name: payload.student_name,
        student_id: payload.student_id,
        notes: payload.notes,
        parent_name: payload.parent_name
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        setEditBookingSlot(null);
        setEditForm({ student_name: '', student_id: '', notes: '', parent_name: '' });
        setShowEditStudentDropdown(false);
        toast.success('Buchung aktualisiert');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Speichern');
      }
    }
  );

  // Delete slot mutation
  const deleteSlotMutation = useMutation(
    async (slotId: number) => {
      await api.delete(`/appointments/slots/${slotId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        toast.success('Slot gelöscht');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Löschen');
      }
    }
  );

  const deleteGlobalFreeSlotsMutation = useMutation(
    async (configId: number) => {
      const response = await api.delete(`/appointments/configs/${configId}/global/free-slots`);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['appointments', 'configs']);
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        const deletedCount = Number(data?.data?.deleted_slots_count || 0);
        toast.success(
          deletedCount > 0
            ? `${deletedCount} freie Slots gelöscht`
            : 'Keine freien Slots zum Löschen gefunden'
        );
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Löschen der freien Slots');
      }
    }
  );

  const refillGlobalSlotsMutation = useMutation(
    async (configId: number) => {
      const response = await api.post(`/appointments/configs/${configId}/global/refill-slots`);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['appointments', 'configs']);
        queryClient.invalidateQueries(['appointments', 'slots', selectedConfigId]);
        const createdCount = Number(data?.data?.created_slots_count || 0);
        toast.success(
          createdCount > 0
            ? `${createdCount} Slots gemäß globalem Zeitfenster ergänzt`
            : 'Keine fehlenden zukünftigen Slots zum Ergänzen'
        );
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Ergänzen der Slots');
      }
    }
  );

  // Create global event mutation (Admin/SL)
  const createGlobalEventMutation = useMutation(
    async (data: typeof globalEventForm) => {
      const start_time = `${data.start_date}T${data.start_time}`;
      const end_time = `${data.end_date}T${data.end_time}`;
      const response = await api.post('/appointments/global/events', {
        title: data.title,
        description: data.description || undefined,
        start_time,
        end_time,
        slot_duration_minutes: data.slot_duration_minutes,
        teacher_ids: data.teacher_ids,
        create_public_token: data.create_public_token
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['appointments', 'global', 'events']);
        queryClient.invalidateQueries(['appointments', 'configs']);
        setShowGlobalEventModal(false);
        setGlobalEventForm({
          title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
          slot_duration_minutes: 15, teacher_ids: [], create_public_token: false
        });
        toast.success(`Globaler Anlass erstellt: ${data.slots_created} Slots für ${data.teacher_count} Lehrer.`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
      }
    }
  );

  // Update global event mutation (Admin/SL) – bei require_confirm erneut mit confirm_delete_bookings senden
  const updateGlobalEventMutation = useMutation(
    async (payload: { eventId: number; data: typeof globalEventForm; confirm_delete_bookings?: boolean }) => {
      const { eventId, data, confirm_delete_bookings } = payload;
      const start_time = `${data.start_date}T${data.start_time}`;
      const end_time = `${data.end_date}T${data.end_time}`;
      const response = await api.put(`/appointments/global/events/${eventId}`, {
        title: data.title,
        description: data.description || undefined,
        start_time,
        end_time,
        slot_duration_minutes: data.slot_duration_minutes,
        teacher_ids: data.teacher_ids,
        confirm_delete_bookings: confirm_delete_bookings || false
      });
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['appointments', 'global', 'events']);
        queryClient.invalidateQueries(['appointments', 'global', 'event', variables.eventId]);
        queryClient.invalidateQueries(['appointments', 'global', 'matrix', variables.eventId]);
        queryClient.invalidateQueries(['appointments', 'configs']);
        setShowGlobalEventModal(false);
        setEditingEventId(null);
        setGlobalEventForm({
          title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
          slot_duration_minutes: 15, teacher_ids: [], create_public_token: false
        });
        toast.success('Anlass aktualisiert');
      },
      onError: (error: any, variables: { eventId: number; data: typeof globalEventForm; confirm_delete_bookings?: boolean }) => {
        const err = error.response?.data;
        if (err?.require_confirm && variables && !variables.confirm_delete_bookings) {
          const msg = err.error || `${err.bookings_affected} Buchung(en) wären betroffen. Möchten Sie die Änderung trotzdem durchführen? (Die betroffenen Buchungen werden storniert.)`;
          if (window.confirm(msg + '\n\nKlicken Sie OK, um die Änderung zu bestätigen und die Buchungen zu stornieren.')) {
            updateGlobalEventMutation.mutate({
              eventId: variables.eventId,
              data: variables.data,
              confirm_delete_bookings: true
            });
          }
          return;
        }
        toast.error(err?.error || 'Fehler beim Aktualisieren');
      }
    }
  );

  const handleUpdateGlobalEvent = () => {
    if (editingEventId == null) return;
    updateGlobalEventMutation.mutate({ eventId: editingEventId, data: globalEventForm });
  };

  // Delete global event mutation (Admin/SL)
  const deleteGlobalEventMutation = useMutation(
    async (eventId: number) => {
      const response = await api.delete(`/appointments/global/events/${eventId}`);
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['appointments', 'global', 'events']);
        queryClient.invalidateQueries(['appointments', 'configs']);
        const sent = Number(data?.data?.cancellation_mails_sent || 0);
        const failed = Number(data?.data?.cancellation_mails_failed || 0);
        if (sent > 0 || failed > 0) {
          toast.success(
            `Globaler Anlass gelöscht. Storno-Mails: ${sent} gesendet${failed > 0 ? `, ${failed} fehlgeschlagen` : ''}.`
          );
        } else {
          toast.success('Globaler Anlass gelöscht');
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler beim Löschen');
      }
    }
  );

  const selectedConfig = configs?.find(c => c.id === selectedConfigId);
  const publicUrl = selectedConfig
    ? `${window.location.origin}/appointments/${selectedConfig.public_token}`
    : '';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Link kopiert');
  };
  const handleDeleteGlobalEvent = async (eventId: number) => {
    try {
      const response = await api.get(`/appointments/global/events/${eventId}/delete-impact`);
      const slotsCount = Number(response.data?.data?.slots_count || 0);
      const teacherCount = Number(response.data?.data?.teacher_count || 0);
      const assignedCount = Number(response.data?.data?.assigned_count || 0);

      const confirmed = window.confirm(
        `Warnung: Es werden ${slotsCount} Termine von ${teacherCount} Lehrern gelöscht.\n` +
        `${assignedCount} Termine sind bereits vergeben und werden rückabgewickelt.\n` +
        'Dieser Vorgang kann nicht rückgängig gemacht werden.\n\n' +
        'Möchten Sie den globalen Anlass wirklich löschen?'
      );
      if (!confirmed) return;
      deleteGlobalEventMutation.mutate(eventId);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Lösch-Auswirkung konnte nicht ermittelt werden');
    }
  };
  const handleDeleteGlobalFreeSlots = async (configId: number) => {
    try {
      const response = await api.get(`/appointments/configs/${configId}/global/free-slots/count`);
      const freeSlotsCount = Number(response.data?.data?.free_slots_count || 0);
      if (freeSlotsCount <= 0) {
        toast('Keine freien Slots zum Löschen vorhanden');
        return;
      }
      const confirmed = window.confirm(
        `Möchten Sie wirklich ${freeSlotsCount} freie Slots löschen? Bereits gebuchte Termine bleiben erhalten.`
      );
      if (!confirmed) return;
      deleteGlobalFreeSlotsMutation.mutate(configId);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Freie Slots konnten nicht geprüft werden');
    }
  };

  const escapeHtml = (value: unknown): string =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const printMatrix = () => {
    if (!matrixData) return;
    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) {
      toast.error('Popup blockiert. Bitte Popups erlauben, um die Matrix zu drucken.');
      return;
    }

    const headerCells = matrixVisibleTeachers
      .map((t) => `<th>${escapeHtml(t.teacher_name)}</th>`)
      .join('');
    const bodyRows = matrixVisibleRows
      .map((row) => {
        const cells = matrixVisibleTeachers
          .map((t) => {
            const cell = row.cells.find((c) => c.config_id === t.config_id);
            if (!cell || cell.slot_id == null) return '<td>–</td>';
            if (cell.status === 'booked' || cell.status === 'reserved') {
              const parent = cell.parent_name ? escapeHtml(cell.parent_name) : '';
              const student = cell.student_name ? `<br><small>${escapeHtml(cell.student_name)}</small>` : '';
              return `<td>${parent || 'Belegt'}${student}</td>`;
            }
            return '<td>Frei</td>';
          })
          .join('');
        return `<tr><td>${escapeHtml(format(parseISO(row.time), 'dd.MM. HH:mm', { locale: de }))}</td>${cells}</tr>`;
      })
      .join('');

    popup.document.write(`
      <!doctype html>
      <html lang="de">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(matrixData.event?.title || 'Matrix')}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #111; }
          h1 { margin: 0 0 8px; font-size: 22px; }
          .meta { margin-bottom: 16px; color: #555; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
          th, td { border: 1px solid #d1d5db; padding: 6px; vertical-align: top; word-break: break-word; }
          th { background: #f3f4f6; text-align: left; }
          tr:nth-child(even) td { background: #fafafa; }
          @media print { body { margin: 10mm; } }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(matrixData.event?.title ? `${matrixData.event.title} – Matrix` : 'Matrix')}</h1>
        <div class="meta">Stand: ${escapeHtml(format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de }))}</div>
        <table>
          <thead>
            <tr><th>Zeit</th>${headerCells}</tr>
          </thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <>
    <div className="p-6 max-w-7xl mx-auto print:hidden">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-gray-50">Elternsprechtags-/Sprechstunden-Verwaltung</h1>
        <p className="text-gray-600 dark:text-gray-400">Verwalten Sie Ihre Terminbuchungen und Zeitfenster</p>
      </div>

      {/* Globale Anlässe (nur Admin / Schulleitung) */}
      {isAdminOrSchulleitung && (
        <div className="mb-6 card p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">Globale Anlässe</h2>
            <button
              onClick={() => {
                setEditingEventId(null);
                setShowGlobalEventModal(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Globalen Anlass anlegen
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Erstellen Sie einen gemeinsamen Anlass (z. B. Elternsprechtag) und legen Sie für ausgewählte Lehrer einheitlich Slots an.
            Über <strong>Matrix</strong> bei jedem Anlass sehen Sie die Gesamtübersicht: pro Lehrer eine Spalte, Zeilen = Slots (frei/belegt inkl. Buchungsname).
          </p>
          {globalEventsLoading ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">Laden...</div>
          ) : (
            <div className="space-y-2">
              {globalEvents?.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">Noch keine globalen Anlässe angelegt.</div>
              )}
              {globalEvents?.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-center justify-between p-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                >
                  <div>
                    <div className="font-semibold">{ev.title}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {format(parseISO(ev.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} – {format(parseISO(ev.end_time), 'HH:mm', { locale: de })} • {ev.slot_duration_minutes} Min • {ev.teacher_count} Lehrer
                    </div>
                    {ev.public_token && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Gemeinsamer Link:</span>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/appointments/event/${ev.public_token}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Link kopiert');
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Link kopieren
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingEventId(ev.id);
                        setShowGlobalEventModal(true);
                      }}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                      title="Anlass bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                      <span className="text-sm font-medium">Bearbeiten</span>
                    </button>
                    <button
                      onClick={() => setMatrixEventId(ev.id)}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded border border-indigo-200 dark:border-indigo-800"
                      title="Gesamtübersicht: alle Lehrer, alle Slots (frei/belegt)"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      <span className="text-sm font-medium">Matrix</span>
                    </button>
                    <button
                      onClick={() => {
                        void handleDeleteGlobalEvent(ev.id);
                      }}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Globalen Anlass löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Matrix-Modal für globales Event (Admin/SL) – mit Filter; dynamisch scrollbar */}
      {matrixEventId != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="card shadow-xl max-w-[95vw] w-full max-h-[92vh] min-w-[980px] min-h-[620px] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {matrixData?.event?.title ? `${matrixData.event.title} – Matrix` : 'Matrix'}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={printMatrix}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <Printer className="w-4 h-4" />
                  Matrix drucken
                </button>
                <button
                  onClick={() => setMatrixEventId(null)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"
                  title="Schließen"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Filterleiste */}
            {matrixData?.teachers?.length ? (
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Filter className="w-4 h-4" />
                  Filter
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Lehrer:</span>
                  {matrixData.teachers.map((t) => {
                    const isSelected = matrixFilterTeacherIds.length === 0 || matrixFilterTeacherIds.includes(t.config_id);
                    return (
                      <label key={t.config_id} className="flex items-center gap-1 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            setMatrixFilterTeacherIds((prev) =>
                              prev.length === 0
                                ? matrixData.teachers!.filter((x) => x.config_id !== t.config_id).map((x) => x.config_id)
                                : isSelected
                                  ? prev.filter((id) => id !== t.config_id)
                                  : [...prev, t.config_id]
                            );
                          }}
                          className="rounded"
                        />
                        <span className="whitespace-nowrap">{t.teacher_name}</span>
                      </label>
                    );
                  })}
                  {matrixFilterTeacherIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setMatrixFilterTeacherIds([])}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Alle anzeigen
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Anzeige:</span>
                  <select
                    value={matrixFilterStatus}
                    onChange={(e) => setMatrixFilterStatus(e.target.value as 'all' | 'free' | 'booked')}
                    className="select input-field px-2 py-1 text-sm"
                  >
                    <option value="all">Alle Zeilen</option>
                    <option value="free">Nur Zeilen mit freien Slots</option>
                    <option value="booked">Nur Zeilen mit Buchungen</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Suche (Eltern/Schüler):</span>
                  <input
                    type="text"
                    value={matrixFilterSearch}
                    onChange={(e) => setMatrixFilterSearch(e.target.value)}
                    placeholder="Name eingeben..."
                    className="input-field px-2 py-1 text-sm w-40"
                  />
                  {matrixFilterSearch && (
                    <button type="button" onClick={() => setMatrixFilterSearch('')} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm">
                      ×
                    </button>
                  )}
                </div>
              </div>
            ) : null}

            <div className="p-4 overflow-auto flex-1 min-h-0">
              {matrixLoading ? (
                <div className="text-center py-8">Matrix wird geladen...</div>
              ) : matrixVisibleRows.length > 0 || matrixData?.rows?.length ? (
                <div className="overflow-auto max-h-[calc(92vh-9rem)] min-h-[380px]">
                  {matrixVisibleRows.length === 0 ? (
                    <div className="text-center py-6 text-amber-600 dark:text-amber-400">Keine Zeilen entsprechen den Filtern. Filter anpassen oder zurücksetzen.</div>
                  ) : (
                    <table className="w-full min-w-[980px] border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '120px' }} />
                        {matrixVisibleTeachers.map((t) => (
                          <col key={t.config_id} style={{ width: matrixVisibleTeachers.length > 0 ? `calc((100% - 120px) / ${matrixVisibleTeachers.length})` : undefined }} />
                        ))}
                      </colgroup>
                      <thead className="sticky top-0 z-20 bg-white dark:bg-gray-900 shadow-[0_1px_0_0_rgba(0,0,0,0.1)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.08)]">
                        <tr>
                          <th className="border border-gray-200 dark:border-gray-700 p-2 text-left bg-gray-100 dark:bg-gray-800 sticky left-0 z-30 w-[120px] min-w-[120px]">Zeit</th>
                          {matrixVisibleTeachers.map((t) => (
                            <th key={t.config_id} className="border border-gray-200 dark:border-gray-700 p-0 text-center bg-gray-100 dark:bg-gray-800 relative" style={{ height: '220px', minHeight: '220px', minWidth: '72px' }}>
                              <span
                                className="absolute whitespace-nowrap"
                                style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)', transformOrigin: 'center center' }}
                                title={t.teacher_name}
                              >
                                {t.teacher_name}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {matrixVisibleRows.map((row, idx) => (
                          <tr key={idx}>
                            <td className="border border-gray-200 dark:border-gray-700 p-2 font-medium sticky left-0 bg-white dark:bg-gray-900 z-10 w-[120px] min-w-[120px] align-middle">
                              {format(parseISO(row.time), 'dd.MM. HH:mm', { locale: de })}
                            </td>
                            {matrixVisibleTeachers.map((t) => {
                              const cell = row.cells.find((c) => c.config_id === t.config_id);
                              if (!cell) return <td key={t.config_id} className="border border-gray-200 dark:border-gray-700 p-2 text-center overflow-hidden align-middle"><span className="text-gray-400 dark:text-gray-500">–</span></td>;
                              return (
                                <td key={t.config_id} className="border border-gray-200 dark:border-gray-700 p-2 text-center overflow-hidden align-middle" style={{ minWidth: '72px' }}>
                                  {cell.slot_id == null ? (
                                    <span className="text-gray-400 dark:text-gray-500">–</span>
                                  ) : cell.status === 'booked' || cell.status === 'reserved' ? (
                                    <div className="bg-amber-50 dark:bg-amber-950/40 rounded p-1 text-left inline-block min-w-0 w-full">
                                      {cell.parent_name && <div className="font-medium truncate" title={cell.parent_name}>{cell.parent_name}</div>}
                                      {cell.student_name && (
                                        cell.student_id
                                          ? <div className="text-xs text-gray-600 dark:text-gray-400 truncate"><Link to={`/Akten?student=${encodeURIComponent(cell.student_id)}`} className="text-blue-600 dark:text-blue-400 hover:underline border border-blue-200 dark:border-blue-800 rounded px-1 bg-white dark:bg-gray-900" title="Schülerakte öffnen">{cell.student_name}</Link></div>
                                          : <div className="text-xs text-gray-600 dark:text-gray-400 truncate" title={cell.student_name}>{cell.student_name}</div>
                                      )}
                                      {!cell.parent_name && !cell.student_name && <span className="text-amber-700 dark:text-amber-300">Belegt</span>}
                                    </div>
                                  ) : (
                                    <span className="text-green-600 dark:text-green-400">Frei</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Keine Slots für dieses Event.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Config List */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">Anlässe/Events</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Neu
              </button>
            </div>

            {configsLoading ? (
              <div className="text-center py-8">Laden...</div>
            ) : (
              <div className="space-y-2">
                {configs?.map(config => (
                  <div
                    key={config.id}
                    onClick={() => setSelectedConfigId(config.id)}
                    className={`p-3 rounded cursor-pointer border-2 transition ${
                      selectedConfigId === config.id
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="font-semibold">{config.title}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {config.slot_duration_minutes} Min • {config.appointment_type === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde'}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {config.global_event_id != null && (
                        <span className="text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200 px-2 py-1 rounded">Globaler Anlass</span>
                      )}
                      {config.is_active ? (
                        <span className="text-xs bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200 px-2 py-1 rounded">Aktiv</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded">Inaktiv</span>
                      )}
                      {config.global_event_id == null ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConfigMutation.mutate(config.id);
                          }}
                          className="ml-auto text-red-600 hover:text-red-800"
                          title="Anlass löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              refillGlobalSlotsMutation.mutate(config.id);
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Fehlende Slots gemäß globalem Zeitfenster ergänzen"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDeleteGlobalFreeSlots(config.id);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Eigene freie Slots dieses globalen Anlasses löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Slot Management */}
        <div className="lg:col-span-2">
          {selectedConfig ? (
            <div className="card p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-50">{selectedConfig.title}</h2>
                  <p className="text-gray-600 dark:text-gray-400">{selectedConfig.description || 'Keine Beschreibung'}</p>
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedConfig.slot_duration_minutes} Minuten
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {selectedConfig.appointment_type === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (selectedConfig.global_event_id != null) {
                        refillGlobalSlotsMutation.mutate(selectedConfig.id);
                        return;
                      }
                      setShowSlotModal(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    {selectedConfig.global_event_id != null ? 'Slots ergänzen' : 'Slots hinzufügen'}
                  </button>
                  <button
                    onClick={() => setShowEmailModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Link senden
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedConfig || !selectedConfig.is_active) {
                        toast.error('Bitte wählen Sie eine aktive Konfiguration aus');
                        return;
                      }
                      // Navigate to Elternbrief with appointment config ID in URL params
                      navigate(`/elternbriefe/neu?appointment_config_id=${selectedConfig.id}`);
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Elternbrief
                  </button>
                </div>
              </div>

              {/* Public URL */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-transparent dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-semibold mb-2">Öffentlicher Link:</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 break-all mb-3">{publicUrl}</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyToClipboard(publicUrl)}
                        className="px-3 py-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" />
                        Kopieren
                      </button>
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Öffnen
                      </a>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center mb-1">QR-Code</div>
                    <div className="bg-white p-2 rounded border border-gray-200 dark:border-gray-600" id={`qr-code-${selectedConfig.id}`}>
                      <QRCodeSVG 
                        value={publicUrl} 
                        size={120}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <button
                      onClick={() => {
                        // Download QR Code as PNG
                        const qrContainer = document.getElementById(`qr-code-${selectedConfig.id}`);
                        if (qrContainer) {
                          const svg = qrContainer.querySelector('svg');
                          if (svg) {
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const img = new Image();
                            
                            img.onload = () => {
                              canvas.width = img.width;
                              canvas.height = img.height;
                              ctx?.drawImage(img, 0, 0);
                              canvas.toBlob((blob) => {
                                if (blob) {
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `qr-code-${selectedConfig.public_token}.png`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  URL.revokeObjectURL(url);
                                  toast.success('QR-Code heruntergeladen');
                                }
                              }, 'image/png');
                            };
                            
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                          }
                        }
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                    >
                      Download QR-Code
                    </button>
                  </div>
                </div>
              </div>

              {/* Slots List */}
              <div>
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Zeitfenster</h3>
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    <Printer className="w-4 h-4" />
                    Liste drucken
                  </button>
                </div>
                {slotsLoading ? (
                  <div className="text-center py-8">Laden...</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {slots?.map(slot => (
                      <div
                        key={slot.id}
                        className={`p-4 rounded border-2 ${
                          slot.status === 'available'
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40'
                            : slot.status === 'reserved'
                            ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/40'
                            : slot.status === 'booked'
                            ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40'
                            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold">
                              {format(parseISO(slot.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} -{' '}
                              {format(parseISO(slot.end_time), 'HH:mm', { locale: de })}
                            </div>
                            {slot.status === 'booked' && slot.parent_name && (
                              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                <div>Elternteil: {slot.parent_name}</div>
                                {slot.student_name && (
                                  <div>Schüler: {slot.student_id
                                    ? <Link to={`/Akten?student=${encodeURIComponent(slot.student_id)}`} className="text-blue-600 dark:text-blue-400 hover:underline" title="Schülerakte öffnen">{slot.student_name}</Link>
                                    : slot.student_name}
                                  </div>
                                )}
                                {slot.parent_email && <div className="text-xs">{slot.parent_email}</div>}
                                {slot.notes && (
                                  <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Notizen:</div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{slot.notes}</div>
                                  </div>
                                )}
                                {slot.booking_id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (confirm('Möchten Sie diesen Termin wirklich stornieren? Der Buchende erhält eine E-Mail.')) {
                                        if (slot.booking_id) {
                                          deleteBookingMutation.mutate(slot.booking_id);
                                        }
                                      }
                                    }}
                                    className="mt-2 text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Stornieren
                                  </button>
                                )}
                              </div>
                            )}
                            {slot.status === 'reserved' && (
                              <div className="text-sm text-yellow-600 mt-1">
                                Reserviert (bis {slot.reserved_until ? format(parseISO(slot.reserved_until), 'HH:mm', { locale: de }) : 'unbekannt'})
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {slot.status === 'available' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setManualSlotId(slot.id);
                                    setManualForm({ student_name: '', student_id: '', notes: '', parent_name: '', parent_email: '' });
                                  setShowManualStudentDropdown(false);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                                title="Schüler/Notiz manuell eintragen (z. B. ohne Internet)"
                              >
                                <UserPlus className="w-3.5 h-3.5" />
                                Schüler eintragen
                              </button>
                            )}
                            {slot.status === 'booked' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditBookingSlot(slot);
                                  setEditForm({
                                    student_name: slot.student_name ?? '',
                                    student_id: slot.student_id ?? '',
                                    notes: slot.notes ?? '',
                                    parent_name: slot.parent_name ?? ''
                                  });
                                  setShowEditStudentDropdown(false);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                title="Schüler/Notiz bearbeiten"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                Bearbeiten
                              </button>
                            )}
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                slot.status === 'available'
                                  ? 'bg-green-200 text-green-800 dark:bg-green-900/60 dark:text-green-200'
                                  : slot.status === 'reserved'
                                  ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-200'
                                  : slot.status === 'booked'
                                  ? 'bg-blue-200 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200'
                                  : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              }`}
                            >
                              {slot.status === 'available'
                                ? 'Verfügbar'
                                : slot.status === 'reserved'
                                ? 'Reserviert'
                                : slot.status === 'booked'
                                ? 'Gebucht'
                                : 'Abgeschlossen'}
                            </span>
                            {(slot.status === 'available' || slot.status === 'reserved') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Möchten Sie diesen Slot wirklich löschen?')) {
                                    deleteSlotMutation.mutate(slot.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Slot löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {slots?.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">Noch keine Slots erstellt</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Wählen Sie einen Anlass/Event aus oder erstellen Sie einen neuen</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Schüler/Notiz manuell eintragen (freier Slot) */}
      {manualSlotId != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-50">Schüler manuell eintragen</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Für Termine, die z. B. ohne Internet gebucht werden (z. B. persönlich in der Schule).
            </p>
            <div className="space-y-3">
              <div ref={manualStudentDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schüler in Datenbank suchen</label>
                <input
                  type="text"
                  value={manualForm.student_name}
                  onChange={(e) => {
                    setManualForm((f) => ({ ...f, student_name: e.target.value, student_id: '', parent_email: '' }));
                    setShowManualStudentDropdown(true);
                  }}
                  onFocus={() => manualStudentSuggestions.length > 0 && setShowManualStudentDropdown(true)}
                  className="input-field w-full"
                  placeholder="Name eingeben (min. 2 Zeichen), dann aus Liste wählen oder freien Namen eintragen"
                />
                {showManualStudentDropdown && manualStudentSuggestions.length > 0 && (
                  <ul className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-lg max-h-48 overflow-y-auto">
                    {manualStudentSuggestions.map((s) => (
                      <li
                        key={s.id}
                        className="px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-0 text-sm"
                        onClick={() => {
                          setManualForm((f) => ({ ...f, student_name: s.name, student_id: s.id, parent_email: '' }));
                          setShowManualStudentDropdown(false);
                        }}
                      >
                        <span className="font-medium">{s.name}</span>
                        {s.klasse && <span className="text-gray-500 dark:text-gray-400 ml-2">({s.klasse})</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {manualForm.student_id && (
                  <p className="mt-1 text-xs text-green-600">Schüler aus Datenbank zugeordnet</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Elternteil / Ansprechpartner (optional)</label>
                <input
                  type="text"
                  value={manualForm.parent_name}
                  onChange={(e) => setManualForm((f) => ({ ...f, parent_name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="z. B. Frau Mustermann"
                />
              </div>
              {manualForm.student_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kontakt-E-Mail für Hinweis</label>
                  {manualContactEmailsLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Lade hinterlegte E-Mail-Adressen…</p>
                  ) : manualContactEmails.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Keine hinterlegte Kontakt-E-Mail gefunden. Es wird keine Hinweis-Mail versendet.</p>
                  ) : manualContactEmails.length === 1 ? (
                    <p className="text-sm text-green-700">
                      Hinweis-Mail wird automatisch an <strong>{manualAutoEmail || manualContactEmails[0]}</strong> gesendet.
                    </p>
                  ) : (
                    <>
                      <select
                        value={manualForm.parent_email}
                        onChange={(e) => setManualForm((f) => ({ ...f, parent_email: e.target.value }))}
                        className="input-field w-full"
                      >
                        <option value="">Bitte E-Mail-Adresse auswählen</option>
                        {manualContactEmails.map((email) => (
                          <option key={email} value={email}>{email}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-amber-700">Bei mehreren Adressen ist eine Auswahl verpflichtend.</p>
                    </>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notiz (optional)</label>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm((f) => ({ ...f, notes: e.target.value }))}
                  className="input-field w-full"
                  rows={3}
                  placeholder="z. B. Rückruf gewünscht"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setManualSlotId(null); setManualForm({ student_name: '', student_id: '', notes: '', parent_name: '', parent_email: '' }); setShowManualStudentDropdown(false); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => {
                  if (manualRequiresEmailSelection && !manualForm.parent_email.trim()) {
                    toast.error('Bitte wählen Sie eine Kontakt-E-Mail-Adresse aus');
                    return;
                  }
                  manualBookingMutation.mutate({
                    slotId: manualSlotId,
                    student_name: manualForm.student_name.trim() || undefined,
                    student_id: manualForm.student_id.trim() || undefined,
                    notes: manualForm.notes.trim() || undefined,
                    parent_name: manualForm.parent_name.trim() || undefined,
                    parent_email: manualForm.parent_email.trim() || undefined
                  });
                }}
                disabled={manualBookingMutation.isLoading || (manualRequiresEmailSelection && !manualForm.parent_email.trim())}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {manualBookingMutation.isLoading ? 'Wird gespeichert…' : 'Eintragen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Buchung bearbeiten (Schüler/Notiz) */}
      {editBookingSlot != null && editBookingSlot.booking_id != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-50">Buchung bearbeiten</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {format(parseISO(editBookingSlot.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} – {format(parseISO(editBookingSlot.end_time), 'HH:mm', { locale: de })}
            </p>
            <div className="space-y-3">
              <div ref={editStudentDropdownRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schüler in Datenbank suchen</label>
                <input
                  type="text"
                  value={editForm.student_name}
                  onChange={(e) => {
                    setEditForm((f) => ({ ...f, student_name: e.target.value, student_id: '' }));
                    setShowEditStudentDropdown(true);
                  }}
                  onFocus={() => editStudentSuggestions.length > 0 && setShowEditStudentDropdown(true)}
                  className="input-field w-full"
                  placeholder="Name eingeben (min. 2 Zeichen), dann aus Liste wählen oder freien Namen eintragen"
                />
                {showEditStudentDropdown && editStudentSuggestions.length > 0 && (
                  <ul className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-lg max-h-48 overflow-y-auto">
                    {editStudentSuggestions.map((s) => (
                      <li
                        key={s.id}
                        className="px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-0 text-sm"
                        onClick={() => {
                          setEditForm((f) => ({ ...f, student_name: s.name, student_id: s.id }));
                          setShowEditStudentDropdown(false);
                        }}
                      >
                        <span className="font-medium">{s.name}</span>
                        {s.klasse && <span className="text-gray-500 dark:text-gray-400 ml-2">({s.klasse})</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {editForm.student_id && (
                  <p className="mt-1 text-xs text-green-600">Schüler aus Datenbank zugeordnet</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Elternteil / Ansprechpartner</label>
                <input
                  type="text"
                  value={editForm.parent_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, parent_name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="z. B. Frau Mustermann"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notiz</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                  className="input-field w-full"
                  rows={3}
                  placeholder="z. B. Rückruf gewünscht"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setEditBookingSlot(null); setEditForm({ student_name: '', student_id: '', notes: '', parent_name: '' }); setShowEditStudentDropdown(false); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => {
                  updateBookingMutation.mutate({
                    bookingId: editBookingSlot.booking_id!,
                    student_name: editForm.student_name.trim(),
                    student_id: editForm.student_id.trim() || undefined,
                    notes: editForm.notes.trim(),
                    parent_name: editForm.parent_name.trim()
                  });
                }}
                disabled={updateBookingMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {updateBookingMutation.isLoading ? 'Wird gespeichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Config Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-50">Neuer Anlass/Event</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                <input
                  type="text"
                  value={newConfig.title}
                  onChange={(e) => setNewConfig({ ...newConfig, title: e.target.value })}
                  className="input-field w-full"
                  placeholder={generateTitle(newConfig.appointment_type, teacherName) || "z.B. Elternsprechtag 2024"}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Format: "{newConfig.appointment_type === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde'} - {teacherName || 'Ihr Name'}"
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung (optional)</label>
                <textarea
                  value={newConfig.description}
                  onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                  placeholder={teacherName || ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slot-Dauer (Minuten)</label>
                <input
                  type="number"
                  value={newConfig.slot_duration_minutes}
                  onChange={(e) => setNewConfig({ ...newConfig, slot_duration_minutes: parseInt(e.target.value) || 15 })}
                  className="input-field w-full"
                  min={5}
                  max={120}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Typ</label>
                <select
                  value={newConfig.appointment_type}
                  onChange={(e) => setNewConfig({ ...newConfig, appointment_type: e.target.value as any })}
                  className="input-field w-full"
                >
                  <option value="parent_teacher_conference">Elternsprechtag</option>
                  <option value="office_hours">Sprechstunde</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormInitialized(false);
                  setNewConfig({ title: '', description: '', slot_duration_minutes: 15, appointment_type: 'parent_teacher_conference' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  createConfigMutation.mutate(newConfig);
                }}
                disabled={!newConfig.title || createConfigMutation.isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Slots Modal */}
      {showSlotModal && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-50">Zeitfenster erstellen</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Es werden automatisch Slots à {selectedConfig.slot_duration_minutes} Minuten erstellt.
                </p>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Startzeit</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Datum</label>
                    <input
                      type="date"
                      value={newSlots.start_date}
                      onChange={(e) => setNewSlots({ ...newSlots, start_date: e.target.value })}
                      className="input-field w-full text-sm"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Uhrzeit</label>
                    <select
                      value={newSlots.start_time}
                      onChange={(e) => setNewSlots({ ...newSlots, start_time: e.target.value })}
                      className="input-field w-full text-sm"
                    >
                      <option value="">-- Uhrzeit wählen --</option>
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Endzeit</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Datum</label>
                    <input
                      type="date"
                      value={newSlots.end_date}
                      onChange={(e) => {
                        setNewSlots({ ...newSlots, end_date: e.target.value });
                        // Auto-fill start date if end date is set and start date is empty
                        if (!newSlots.start_date) {
                          setNewSlots(prev => ({ ...prev, end_date: e.target.value, start_date: e.target.value }));
                        }
                      }}
                      className="input-field w-full text-sm"
                      min={newSlots.start_date || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Uhrzeit</label>
                    <select
                      value={newSlots.end_time}
                      onChange={(e) => setNewSlots({ ...newSlots, end_time: e.target.value })}
                      className="input-field w-full text-sm"
                    >
                      <option value="">-- Uhrzeit wählen --</option>
                      {timeOptions.map((time) => {
                        // If same date, only show times after start time
                        if (newSlots.start_date === newSlots.end_date && newSlots.start_time) {
                          return time > newSlots.start_time ? (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ) : null;
                        }
                        return (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {(newSlots.start_date || newSlots.start_time || newSlots.end_date || newSlots.end_time) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md p-3 text-xs text-gray-600 dark:text-gray-400">
                  <div className="font-semibold mb-1">Vorschau:</div>
                  <div>
                    Start: {newSlots.start_date && newSlots.start_time 
                      ? (() => {
                          try {
                            const dt = combineDateTime(newSlots.start_date, newSlots.start_time);
                            return format(parseISO(dt), 'dd.MM.yyyy HH:mm', { locale: de });
                          } catch {
                            return `${newSlots.start_date} ${newSlots.start_time}`;
                          }
                        })()
                      : (newSlots.start_date ? `${newSlots.start_date} ${newSlots.start_time || ''}`.trim() : 'Bitte Datum und Zeit auswählen')}
                  </div>
                  <div>
                    Ende: {newSlots.end_date && newSlots.end_time 
                      ? (() => {
                          try {
                            const dt = combineDateTime(newSlots.end_date, newSlots.end_time);
                            return format(parseISO(dt), 'dd.MM.yyyy HH:mm', { locale: de });
                          } catch {
                            return `${newSlots.end_date} ${newSlots.end_time}`;
                          }
                        })()
                      : (newSlots.end_date ? `${newSlots.end_date} ${newSlots.end_time || ''}`.trim() : 'Bitte Datum und Zeit auswählen')}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowSlotModal(false);
                  setNewSlots({ start_date: '', start_time: '', end_date: '', end_time: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  const start_time = combineDateTime(newSlots.start_date, newSlots.start_time);
                  const end_time = combineDateTime(newSlots.end_date, newSlots.end_time);
                  createSlotsMutation.mutate({ 
                    configId: selectedConfig.id, 
                    start_time, 
                    end_time 
                  });
                }}
                disabled={!newSlots.start_date || !newSlots.start_time || !newSlots.end_date || !newSlots.end_time || createSlotsMutation.isLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Global Event Modal (Admin/SL) */}
      {showGlobalEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-50">
              {editingEventId != null ? 'Globalen Anlass bearbeiten' : 'Globalen Anlass anlegen'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {editingEventId != null
                ? 'Sie können Titel, Beschreibung, Zeitfenster, Slot-Dauer und die zugehörigen Lehrer anpassen. Bereits vergebene Slots können dabei storniert werden (Hinweis erscheint vor der Änderung).'
                : 'Für alle ausgewählten Lehrer wird derselbe Anlass mit einheitlichem Zeitfenster und Slotdauer erstellt.'}
            </p>
            {editingEventId != null && !editingEventData && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">Anlass wird geladen...</div>
            )}
            {(editingEventId == null || editingEventData) && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                <input
                  type="text"
                  value={globalEventForm.title}
                  onChange={(e) => setGlobalEventForm({ ...globalEventForm, title: e.target.value })}
                  className="input-field w-full"
                  placeholder="z. B. Elternsprechtag März 2026"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung (optional)</label>
                <textarea
                  value={globalEventForm.description}
                  onChange={(e) => setGlobalEventForm({ ...globalEventForm, description: e.target.value })}
                  className="input-field w-full"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Startdatum</label>
                  <input
                    type="date"
                    value={globalEventForm.start_date}
                    onChange={(e) => setGlobalEventForm({ ...globalEventForm, start_date: e.target.value })}
                    className="input-field w-full"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Startzeit</label>
                  <select
                    value={globalEventForm.start_time}
                    onChange={(e) => setGlobalEventForm({ ...globalEventForm, start_time: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="">-- wählen --</option>
                    {timeOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enddatum</label>
                  <input
                    type="date"
                    value={globalEventForm.end_date}
                    onChange={(e) => setGlobalEventForm({ ...globalEventForm, end_date: e.target.value })}
                    className="input-field w-full"
                    min={globalEventForm.start_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Endzeit</label>
                  <select
                    value={globalEventForm.end_time}
                    onChange={(e) => setGlobalEventForm({ ...globalEventForm, end_time: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="">-- wählen --</option>
                    {timeOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slot-Dauer (Minuten)</label>
                <input
                  type="number"
                  value={globalEventForm.slot_duration_minutes}
                  onChange={(e) => setGlobalEventForm({ ...globalEventForm, slot_duration_minutes: parseInt(e.target.value) || 15 })}
                  className="input-field w-full"
                  min={5}
                  max={120}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lehrer auswählen</label>
                <div className="border border-gray-200 dark:border-gray-700 rounded px-3 py-2 max-h-48 overflow-y-auto space-y-1 bg-white dark:bg-gray-900">
                  {!teachersForGlobal?.length && <div className="text-sm text-gray-500 dark:text-gray-400">Laden...</div>}
                  {teachersForGlobal?.map((t) => {
                    const name = [t.last_name, t.first_name].filter(Boolean).join(', ') || t.username;
                    const checked = globalEventForm.teacher_ids.includes(t.id);
                    return (
                      <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGlobalEventForm({ ...globalEventForm, teacher_ids: [...globalEventForm.teacher_ids, t.id] });
                            } else {
                              setGlobalEventForm({ ...globalEventForm, teacher_ids: globalEventForm.teacher_ids.filter((id) => id !== t.id) });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {editingEventId == null && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalEventForm.create_public_token}
                    onChange={(e) => setGlobalEventForm({ ...globalEventForm, create_public_token: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Gemeinsamen Buchungslink erzeugen (optional)</span>
                </label>
              )}
            </div>
            )}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowGlobalEventModal(false);
                  setEditingEventId(null);
                  setGlobalEventForm({
                    title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
                    slot_duration_minutes: 15, teacher_ids: [], create_public_token: false
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              {editingEventId != null ? (
                <button
                  onClick={handleUpdateGlobalEvent}
                  disabled={
                    !globalEventForm.title ||
                    !globalEventForm.start_date ||
                    !globalEventForm.start_time ||
                    !globalEventForm.end_date ||
                    !globalEventForm.end_time ||
                    globalEventForm.teacher_ids.length === 0 ||
                    updateGlobalEventMutation.isLoading
                  }
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Speichern
                </button>
              ) : (
                <button
                  onClick={() => createGlobalEventMutation.mutate(globalEventForm)}
                  disabled={
                    !globalEventForm.title ||
                    !globalEventForm.start_date ||
                    !globalEventForm.start_time ||
                    !globalEventForm.end_date ||
                    !globalEventForm.end_time ||
                    globalEventForm.teacher_ids.length === 0 ||
                    createGlobalEventMutation.isLoading
                  }
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Anlegen
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showEmailModal && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-50">Link per E-Mail senden</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-Mail-Adresse</label>
                <input
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  className="input-field w-full"
                  placeholder="eltern@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nachricht (optional)</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  className="input-field w-full"
                  rows={4}
                  placeholder="Optionale persönliche Nachricht..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailRecipient('');
                  setEmailMessage('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={() => sendLinkMutation.mutate({ configId: selectedConfig.id, recipient_email: emailRecipient, message: emailMessage })}
                disabled={!emailRecipient || sendLinkMutation.isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Senden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helper Modal */}
      <HelperModal
        helperKey="appointments"
        title="Terminbuchungen - Erste Schritte"
        showOnce={true}
        showPermanentOption={true}
        content={
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">Willkommen im Terminbuchungsbereich!</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Hier können Sie Terminbuchungen für Elternsprechtage und Sprechstunden verwalten.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">So funktioniert es:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li><strong>Anlass/Event erstellen:</strong> Klicken Sie auf "Neu", um einen neuen Anlass/Event zu erstellen. Geben Sie Titel, Beschreibung und Slot-Dauer an.</li>
                <li><strong>Zeitfenster hinzufügen:</strong> Wählen Sie einen Anlass/Event aus und klicken Sie auf "Slots hinzufügen". Wählen Sie Start- und Enddatum sowie Uhrzeit.</li>
                <li><strong>Link oder QR-Code teilen:</strong> Jeder Anlass/Event erhält einen öffentlichen Link und QR-Code, den Sie an Eltern weitergeben können.</li>
                <li><strong>Buchungen verwalten:</strong> In der Slot-Übersicht sehen Sie alle Buchungen mit Details zu Elternteil, Schüler und Notizen.</li>
              </ol>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">💡 Tipp</h4>
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                Sie können einen Link direkt per E-Mail senden oder den QR-Code herunterladen und ausdrucken.
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">⚠️ Wichtig</h4>
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                Eltern müssen ihre Buchung per E-Mail-Link bestätigen. Reservierungen laufen nach 10 Minuten automatisch ab.
              </p>
            </div>
          </div>
        }
      />
    </div>

    {/* Nur für Druck: Terminliste (außerhalb von print:hidden) */}
    {selectedConfig && slots && (
      <div className="hidden print:block p-6 text-black">
        <div className="mb-4 pb-3 border-b-2 border-gray-300">
          <h1 className="text-2xl font-bold mb-1">{selectedConfig.title}</h1>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Terminliste · Stand {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            Gesamt: {slots.length} · Gebucht: {slots.filter((s) => s.status === 'booked').length} · Verfügbar: {slots.filter((s) => s.status === 'available').length}
          </p>
        </div>
        <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '17%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">Zeit</th>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">Status</th>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">Elternteil</th>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">Schüler</th>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">E-Mail</th>
              <th className="text-left py-2 pr-3 border-b-2 border-gray-400 font-semibold text-black">Notiz</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot, idx) => (
              <tr
                key={slot.id}
                className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                style={{ breakInside: 'avoid' }}
              >
                <td className="py-2 pr-3 border-b border-gray-200 align-top">
                  {format(parseISO(slot.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} – {format(parseISO(slot.end_time), 'HH:mm', { locale: de })}
                </td>
                <td className="py-2 pr-3 border-b border-gray-200 align-top">
                  {slot.status === 'available'
                    ? 'Verfügbar'
                    : slot.status === 'reserved'
                    ? 'Reserviert'
                    : slot.status === 'booked'
                    ? 'Gebucht'
                    : slot.status}
                </td>
                <td className="py-2 pr-3 border-b border-gray-200 align-top break-words">{slot.status === 'booked' ? (slot.parent_name ?? '–') : '–'}</td>
                <td className="py-2 pr-3 border-b border-gray-200 align-top break-words">{slot.status === 'booked' ? (slot.student_name ?? '–') : '–'}</td>
                <td className="py-2 pr-3 border-b border-gray-200 align-top break-all">{slot.status === 'booked' ? (slot.parent_email ?? '–') : '–'}</td>
                <td className="py-2 pr-3 border-b border-gray-200 align-top whitespace-pre-wrap break-words">{slot.status === 'booked' && slot.notes ? slot.notes : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    </>
  );
}

